import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import { Search, Download, CheckCircle, XCircle, Clock, Receipt } from 'lucide-react';
import InvoiceView from '../components/InvoiceView';

const EPinReport = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [requests, setRequests] = useState([]);
    const [viewInvoiceReqId, setViewInvoiceReqId] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/user/epin_requests.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                    setRequests(data.data);
                }
            } catch (error) {
                console.error("Failed to fetch epin requests", error);
            }
        };
        fetchData();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'bg-green-100 text-green-700';
            case 'Pending': return 'bg-yellow-100 text-yellow-700';
            case 'Rejected': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">E-Pin Request History</h1>
                        <p className="text-gray-500 text-sm mt-1">Track status of your purchase requests</p>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors shadow-sm">
                        <Download size={18} /> Export Report
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search Request ID..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <select className="px-4 py-2 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-gray-700 cursor-pointer">
                            <option>All Status</option>
                            <option>Approved</option>
                            <option>Pending</option>
                            <option>Rejected</option>
                        </select>
                        <select className="px-4 py-2 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-gray-700 cursor-pointer">
                            <option>Last 30 Days</option>
                            <option>Last 3 Months</option>
                        </select>
                    </div>
                </div>

                {/* Table / Card List */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs uppercase font-semibold">
                                <tr>
                                    <th className="px-6 py-4">Request ID</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Details</th>
                                    <th className="px-6 py-4">Amount</th>
                                    <th className="px-6 py-4">Transaction Ref</th>
                                    <th className="px-6 py-4 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {requests.map((req) => (
                                    <tr key={req.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="font-bold text-gray-900">{req.id}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1.5" title="Request Time">
                                                    <Clock size={14} /> {new Date(req.created_at).toLocaleString()}
                                                </div>
                                                {req.transaction_date && (
                                                    <div className="text-xs text-indigo-600 font-medium">
                                                        Txn: {new Date(req.transaction_date).toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">
                                                <span className="font-semibold text-gray-800">{req.quantity} x {req.package}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-gray-900">₹ {parseFloat(req.amount).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-sm font-mono text-gray-500">{req.transaction_id}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusColor(req.status)}`}>
                                                    {req.status === 'Approved' && <CheckCircle size={12} />}
                                                    {req.status === 'Rejected' && <XCircle size={12} />}
                                                    {req.status === 'Pending' && <Clock size={12} />}
                                                    {req.status}
                                                </span>
                                                {req.status === 'Approved' && (
                                                    <button
                                                        onClick={() => setViewInvoiceReqId(req.id)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors"
                                                        title="View Invoice"
                                                    >
                                                        <Receipt size={12} /> Invoice
                                                    </button>
                                                )}
                                            </div>
                                            {req.admin_remarks && (
                                                <p className="text-xs text-red-500 mt-1">{req.admin_remarks}</p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden divide-y divide-gray-100">
                        {requests.length > 0 ? (
                            requests.map((req) => (
                                <div key={req.id} className="p-4 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Request ID</span>
                                            <p className="font-bold text-gray-900">{req.id}</p>
                                        </div>
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${getStatusColor(req.status)}`}>
                                            {req.status}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Details</span>
                                            <p className="text-sm font-semibold text-gray-800">{req.quantity} x {req.package}</p>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Amount</span>
                                            <p className="text-sm font-bold text-gray-900">₹ {parseFloat(req.amount).toLocaleString()}</p>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-3 rounded-xl space-y-2">
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                            <Clock size={14} className="text-gray-400" />
                                            <span>{new Date(req.created_at).toLocaleString()}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                            <Receipt size={14} className="text-gray-400" />
                                            <span className="font-mono">{req.transaction_id || 'N/A'}</span>
                                        </div>
                                    </div>

                                    {req.status === 'Approved' && (
                                        <button
                                            onClick={() => setViewInvoiceReqId(req.id)}
                                            className="w-full py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Receipt size={14} /> View Invoice
                                        </button>
                                    )}

                                    {req.admin_remarks && (
                                        <div className="p-2.5 bg-red-50 border border-red-100 rounded-lg">
                                            <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest mb-0.5">Admin Remarks</p>
                                            <p className="text-xs text-red-600 italic font-medium">{req.admin_remarks}</p>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-400 italic">No request history found</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Invoice View Modal */}
            {viewInvoiceReqId && (
                <InvoiceView
                    referenceId={viewInvoiceReqId}
                    invoiceType="epin"
                    onClose={() => setViewInvoiceReqId(null)}
                    isAdmin={false}
                />
            )}
        </Layout>
    );
};

export default EPinReport;
